import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

import { adminService } from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';

const ProfileCustomization = ({ helperId, isAddMode }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState({
    name: '',
    typeLabel: 'Licensed Professional',
    type: 'licensed',
    bio: '',
    profileImage: '/assets/images/no_image.png',
    profileImageAlt: '',
    expertise: [],
    languages: ['English'],
    location: '',
    availabilityStatus: 'unavailable',
    bookingChargeEnabled: false,
    bookingChargeAmount: '',
    bookingChargeCurrency: 'USD'
  });

  // Load existing helper data if in edit mode
  useEffect(() => {
    if (!isAddMode && helperId) {
      loadHelperData();
    }
  }, [helperId, isAddMode]);

  const loadHelperData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await adminService?.getHelperById(helperId);
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load helper data');
        return;
      }
      
      if (data) {
        setProfileData({
          name: data?.name || '',
          typeLabel: data?.typeLabel || 'Licensed Professional',
          type: data?.type || 'licensed',
          bio: data?.bio || '',
          profileImage: data?.profileImage || '/assets/images/no_image.png',
          profileImageAlt: data?.profileImageAlt || '',
          expertise: data?.expertise || [],
          languages: data?.languages || ['English'],
          location: data?.location || '',
          availabilityStatus: data?.availabilityStatus || 'unavailable',
          bookingChargeEnabled: data?.bookingChargeEnabled || false,
          bookingChargeAmount: data?.bookingChargeAmount || '',
          bookingChargeCurrency: data?.bookingChargeCurrency || 'USD'
        });
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const helperTypes = [
    { value: 'licensed', label: 'Licensed Professional' },
    { value: 'lived-experience', label: 'Lived Experience Helper' }
  ];

  const availabilityOptions = [
    { value: 'available-now', label: 'Available Now' },
    { value: 'available-soon', label: 'Available Soon' },
    { value: 'unavailable', label: 'Unavailable' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'AUD', label: 'Australian Dollar (A$)' },
    { value: 'INR', label: 'Indian Rupee (₹)' },
    { value: 'NZD', label: 'New Zealand Dollar (NZ$)' },
    { value: 'SGD', label: 'Singapore Dollar (S$)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'EUR', label: 'Euro (€)' }
  ];

  const handleImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ 
          ...profileData, 
          profileImage: reader?.result,
          profileImageAlt: profileData?.profileImageAlt || `${profileData?.name} profile picture`
        });
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    // Validation
    if (!profileData?.name?.trim()) {
      setError('Helper name is required');
      return;
    }

    // Validate booking charge if enabled
    if (profileData?.bookingChargeEnabled) {
      if (!profileData?.bookingChargeAmount || parseFloat(profileData?.bookingChargeAmount) <= 0) {
        setError('Please enter a valid booking charge amount');
        return;
      }
    }

    setSaving(true);
    setError('');

    try {
      if (isAddMode) {
        // Create new helper
        const { data, error: createError } = await adminService?.createHelper({
          ...profileData,
          userId: user?.id,
          profileImageAlt: profileData?.profileImageAlt || `${profileData?.name} profile picture`
        });

        if (createError) {
          setError(createError?.message || 'Failed to create helper');
          return;
        }

        alert('Helper profile created successfully!');
        navigate('/admin-console');
      } else {
        // Update existing helper
        const { data, error: updateError } = await adminService?.updateHelper(helperId, profileData);

        if (updateError) {
          setError(updateError?.message || 'Failed to update helper');
          return;
        }

        alert('Helper profile updated successfully!');
        navigate('/admin-console');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading helper data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="User" size={20} className="text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {isAddMode ? 'Create Helper Profile' : 'Edit Helper Profile'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isAddMode ? 'Add a new helper to the platform' : 'Update helper information'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border">
                <Image
                  src={profileData?.profileImage}
                  alt={profileData?.profileImageAlt || 'Helper profile picture'}
                  className="w-full h-full object-cover" />
              </div>
              <label
                htmlFor="profile-image-upload"
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                <Icon name="Camera" size={16} className="text-primary-foreground" />
                <input
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden" />
              </label>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            <Input
              label="Helper Name *"
              type="text"
              value={profileData?.name}
              onChange={(e) => setProfileData({ ...profileData, name: e?.target?.value })}
              placeholder="Enter helper's full name"
              description="This is how seekers will see the helper's name" />

            <Select
              label="Helper Type"
              options={helperTypes}
              value={profileData?.type}
              onChange={(value) => {
                const selectedType = helperTypes?.find(t => t?.value === value);
                setProfileData({ 
                  ...profileData, 
                  type: value,
                  typeLabel: selectedType?.label || 'Licensed Professional',
                  // Reset booking charge if switching to lived-experience
                  bookingChargeEnabled: value === 'licensed' ? profileData?.bookingChargeEnabled : false
                });
              }} />

            {profileData?.type === 'licensed' && (
              <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <Checkbox
                  label="Enable Booking Charge"
                  description="Charge seekers a fee when booking sessions with you"
                  checked={profileData?.bookingChargeEnabled}
                  onChange={(e) => setProfileData({ 
                    ...profileData, 
                    bookingChargeEnabled: e?.target?.checked 
                  })}
                />

                {profileData?.bookingChargeEnabled && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        label="Currency"
                        options={currencyOptions}
                        value={profileData?.bookingChargeCurrency}
                        onChange={(value) => setProfileData({ 
                          ...profileData, 
                          bookingChargeCurrency: value 
                        })}
                      />
                      <Input
                        label="Booking Charge Amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={profileData?.bookingChargeAmount}
                        onChange={(e) => setProfileData({ 
                          ...profileData, 
                          bookingChargeAmount: e?.target?.value 
                        })}
                        placeholder="0.00"
                        description="Amount to charge per booking"
                      />
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
                      <p>
                        This charge will be collected via Stripe when seekers confirm their booking. 
                        The amount will be displayed during the booking process.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Professional Bio</label>
          <textarea
            value={profileData?.bio}
            onChange={(e) => setProfileData({ ...profileData, bio: e?.target?.value })}
            rows={5}
            maxLength={500}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Share experience, approach, and what seekers can expect..." />
          <p className="text-xs text-muted-foreground mt-1">
            {profileData?.bio?.length || 0}/500 characters
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Location"
            type="text"
            value={profileData?.location}
            onChange={(e) => setProfileData({ ...profileData, location: e?.target?.value })}
            placeholder="e.g., New York, NY" />

          <Select
            label="Availability Status"
            options={availabilityOptions}
            value={profileData?.availabilityStatus}
            onChange={(value) => setProfileData({ ...profileData, availabilityStatus: value })} />
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="default" 
            iconName="Save" 
            iconPosition="left" 
            onClick={handleSaveProfile}
            disabled={saving}>
            {saving ? 'Saving...' : (isAddMode ? 'Create Helper' : 'Save Changes')}
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/admin-console')}
            disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCustomization;